import { useState } from 'react';
import { KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';
import { Loader } from '@progress/kendo-react-indicators';
import { useGetAttachmentMutation } from 'common/store/api/datagrid.api';
import { GridCellProps } from '@progress/kendo-react-grid';
import { NotificationType, ToastState } from 'common/models/notification.model';
import { useDispatch } from 'react-redux';
import { showToast } from 'common/store/slices/notification.slice';
import KwelloIconButton from 'common/components/KwelloButton/KwelloIconButton';
import KwelloModal from 'common/components/dialog/KwelloModal';
import { showModal } from 'common/store/slices/modal.slice';
import { ColumnModel } from 'common/models/widget-config.model';

export const AttachmentCell = (props: GridCellProps) => {
  const { imageId, imageFileName, responseId } = props.dataItem;
  const dispatch = useDispatch();
  const [showAttachment, setShowAttachment] = useState(false);
  const [getAttachment] = useGetAttachmentMutation();
  const [imageSource, setImageSource] = useState('');
  const [tooltipValue, setTooltipValue] = useState('View attachment');
  const [fetchingImage, setFetchingImage] = useState(false);

  if (!imageId) {
    return <td />;
  }

  const showErrorToast = (message: string) => {
    const toastNotification: ToastState = {
      id: 'attachment-error',
      type: NotificationType.ERROR,
      text: message,
    };

    dispatch(showToast(toastNotification));
  };

  const onClick = () => {
    setFetchingImage(true);
    setImageSource('');
    getAttachment({ imageId, responseId })
      .then((response) => {
        setFetchingImage(false);

        if (response.data?.base64String) {
          setImageSource(`data:${response.data.fileMimeType};base64,${response.data.base64String}`);
          dispatch(showModal({ modalId: 'ATTACHMENT_CELL_DIALOG' }));
          setShowAttachment(true);
        } else {
          setTooltipValue('attachment not found');
          showErrorToast(`Attachment not found for ${imageFileName}`);
        }
      })
      .catch(() => {
        setTooltipValue('error fetching attachment');

        showErrorToast(`Error fetching ${imageFileName}`);
      });
  };

  const toggleDialog = () => setShowAttachment(false);

  return (
    <>
      <td>
        {!fetchingImage && (
          <KwelloIconButton
            icon={KWELLO_ICON.ATTACHMENT_ICON}
            onClick={onClick}
            tooltip={tooltipValue}
          />
        )}
        {fetchingImage && (
          <span className="loader">
            <Loader size="small" type="pulsing" />
          </span>
        )}
      </td>
      {showAttachment && (
        <KwelloModal
          icon={KWELLO_ICON.ATTACHMENT_ICON}
          id="ATTACHMENT_CELL_DIALOG"
          title={imageFileName}
          onClose={toggleDialog}
        >
          <div className="attachment-container">
            <img alt={imageFileName} src={imageSource} />
          </div>
        </KwelloModal>
      )}
    </>
  );
};

export const AttachmentColumn: ColumnModel = {
  title: 'Attachment',
  field: 'imageId',
  width: '150px',
  groupable: false,
  cell: AttachmentCell,
};
